import { useEffect, useRef } from "react";

function MessagesPage({
  conversations,
  currentUserId,
  detail,
  error,
  isLoading,
  isLoadingDetail,
  isSending,
  isSubmittingReview,
  messageDraft,
  onMessageDraftChange,
  onOpenConversation,
  onReviewCommentChange,
  onReviewRatingChange,
  onReportMessage,
  onSendMessage,
  onSubmitSellerReview,
  reportError,
  reportNotice,
  reportingMessageId,
  reviewComment,
  reviewError,
  reviewNotice,
  reviewRating,
  selectedConversationId,
}) {
  const messageListRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  const shouldFollowLatestMessageRef = useRef(true);

  useEffect(() => {
    const messageList = messageListRef.current;
    const conversationChanged = activeConversationIdRef.current !== detail?.id;
    if (messageList && (conversationChanged || shouldFollowLatestMessageRef.current)) {
      messageList.scrollTop = messageList.scrollHeight;
    }
    activeConversationIdRef.current = detail?.id || null;
  }, [detail?.id, detail?.messages.length]);

  function handleMessageListScroll(event) {
    const messageList = event.currentTarget;
    const distanceFromBottom = messageList.scrollHeight - messageList.clientHeight - messageList.scrollTop;
    shouldFollowLatestMessageRef.current = distanceFromBottom <= 80;
  }

  return (
    <section className="main-panel">
      <div className="page-heading messages-heading">
        <div>
          <span className="mini-label">Beskeder</span>
          <h1>Beskeder</h1>
          <p>Hold samtaler om annoncer samlet i appen.</p>
        </div>
      </div>

      {error && <p className="status-line error">{error}</p>}

      <div className="messages-layout">
        <aside className="conversation-list" aria-label="Samtaler">
          {isLoading ? (
            <div className="empty-results">
              <strong>Henter samtaler...</strong>
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-results">
              <strong>Ingen beskeder endnu</strong>
              <p>Når du kontakter en sælger, eller en køber skriver, vises samtalen her.</p>
            </div>
          ) : (
            conversations.map((conversation) => (
              <button
                className={conversation.id === selectedConversationId ? "active" : ""}
                key={conversation.id}
                onClick={() => onOpenConversation(conversation.id)}
                type="button"
              >
                <div className="conversation-thumb">
                  {conversation.listing.primary_image_url ? (
                    <img src={conversation.listing.primary_image_url} alt="" />
                  ) : (
                    <span>{initials(conversation.listing.title)}</span>
                  )}
                </div>
                <div>
                  <strong>{conversation.listing.title}</strong>
                  <span>{conversation.other_participant.display_name}</span>
                  <p>{conversation.latest_message?.body || "Ingen beskeder endnu."}</p>
                </div>
                {conversation.unread_count > 0 && (
                  <i aria-label={`${conversation.unread_count} ulaeste`}>
                    {conversation.unread_count}
                  </i>
                )}
              </button>
            ))
          )}
        </aside>

        <section className="conversation-thread">
          {!selectedConversationId ? (
            <div className="empty-results">
              <strong>Vælg en samtale</strong>
              <p>Samtalens beskeder vises her.</p>
            </div>
          ) : isLoadingDetail ? (
            <div className="empty-results">
              <strong>Henter samtale...</strong>
            </div>
          ) : detail ? (
            <>
              <div className="thread-header">
                <div>
                  <span className="mini-label">{detail.listing.status}</span>
                  <h2>{detail.listing.title}</h2>
                  <p>
                    {detail.listing.price_amount} {detail.listing.price_currency} |{" "}
                    {threadCounterparty(detail, currentUserId).display_name}
                  </p>
                </div>
              </div>

              <section className="safe-trade-card message-safety" aria-label="Tryg beskedhandel">
                <span className="mini-label">Tryg handel</span>
                <ul>
                  <li>Bliv i beskedtråden, hvis der opstår uenighed.</li>
                  <li>Undgå eksterne betalingslinks og pres for hurtig betaling.</li>
                  <li>Rapporter beskeder, der beder om koder eller private betalingsoplysninger.</li>
                </ul>
              </section>

              {detail.buyer.id === currentUserId && (
                <form className="seller-review-card" onSubmit={onSubmitSellerReview}>
                  <div className="review-form-header">
                    <div>
                      <span className="mini-label">Anmeldelse</span>
                      <h3>Vurder sælgeren</h3>
                    </div>
                    <select
                      onChange={(event) => onReviewRatingChange(Number(event.target.value))}
                      value={reviewRating}
                    >
                      <option value={5}>5 / 5</option>
                      <option value={4}>4 / 5</option>
                      <option value={3}>3 / 5</option>
                      <option value={2}>2 / 5</option>
                      <option value={1}>1 / 5</option>
                    </select>
                  </div>
                  <label>
                    Kort kommentar
                    <textarea
                      onChange={(event) => onReviewCommentChange(event.target.value)}
                      placeholder="Hvordan gik handlen?"
                      value={reviewComment}
                    />
                  </label>
                  {reviewNotice && <p className="status-line notice">{reviewNotice}</p>}
                  {reviewError && <p className="status-line error">{reviewError}</p>}
                  <button disabled={isSubmittingReview} type="submit">
                    {isSubmittingReview ? "Gemmer..." : "Gem anmeldelse"}
                  </button>
                </form>
              )}

              <div className="message-list" onScroll={handleMessageListScroll} ref={messageListRef}>
                {reportNotice && <p className="status-line notice">{reportNotice}</p>}
                {reportError && <p className="status-line error">{reportError}</p>}
                {detail.messages.length === 0 ? (
                  <p className="status-line">Der er ingen beskeder i samtalen endnu.</p>
                ) : (
                  detail.messages.map((message) => {
                    const isMine = message.sender.id === currentUserId;

                    return (
                      <article
                        className={isMine ? "mine" : ""}
                        key={message.id}
                      >
                        <span>{message.sender.display_name}</span>
                        <p>{message.body}</p>
                        {!isMine && (
                          <button
                            className="message-report-button"
                            disabled={reportingMessageId === message.id}
                            onClick={() => onReportMessage(message)}
                            type="button"
                          >
                            {reportingMessageId === message.id ? "Rapporterer..." : "Rapporter"}
                          </button>
                        )}
                      </article>
                    );
                  })
                )}
              </div>

              <form className="message-compose" onSubmit={onSendMessage}>
                <label>
                  Skriv besked
                  <textarea
                    name="message"
                    onChange={(event) => onMessageDraftChange(event.target.value)}
                    placeholder="Skriv til den anden part..."
                    value={messageDraft}
                  />
                </label>
                <button disabled={isSending || !messageDraft.trim()} type="submit">
                  {isSending ? "Sender..." : "Send besked"}
                </button>
              </form>
            </>
          ) : (
            <div className="empty-results">
              <strong>Samtalen kunne ikke vises</strong>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}

function threadCounterparty(detail, currentUserId) {
  return detail.buyer.id === currentUserId ? detail.seller : detail.buyer;
}

function initials(value) {
  return (value || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export default MessagesPage;
